import React, { createRef, Fragment, PureComponent } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { ButtonLink } from '../../../components/Button';
import {
  Carousel,
  CarouselContent,
  CarouselContentWrapper,
  CarouselControl,
  CarouselImage,
  CarouselIndicatorItem,
  CarouselIndicators,
  CarouselIndicatorWrapper,
  CarouselItem
} from '../../../components/Carousel';
import { Column, Row } from '../../../components/Grid';
import { HtmlContent } from '../../../components/layout/HtmlContent';
import { Section } from '../../../components/Section';
import { SvgIcon } from '../../../components/SvgIcon';
import { Title } from '../../../components/Title';
import { TypoImage } from '../../../components/TypoImage';
import {
  withBreakpoints,
  WithBreakpointsProps
} from '../../../provider/BreakpointProvider/withBreakpoints';
import { selectStage } from '../../../provider/Typo3Provider/selectors';
import { CType, Typo3Image } from '../../../provider/Typo3Provider/types';
import {
  withTypo3,
  WithTypo3Props
} from '../../../provider/Typo3Provider/withTypo3';

const Content = ({ header, bodytext, header_link, subheader }: CType) => (
  <CarouselContentWrapper>
    <Section>
      {(header || bodytext || subheader) && (
        <Row noGap>
          <Column large={6} offsetOnLarge={6}>
            <CarouselContent>
              {header && (
                <Title.KeyVisual>
                  <HtmlContent htmlString={header} />
                </Title.KeyVisual>
              )}

              {bodytext && <HtmlContent htmlString={bodytext} />}

              {header_link && subheader && (
                <ButtonLink to={header_link} inlined outlined small>
                  {subheader}
                </ButtonLink>
              )}
            </CarouselContent>
          </Column>
        </Row>
      )}
    </Section>
  </CarouselContentWrapper>
);

interface ControlsProps {
  onClickPrev: () => void;
  onClickNext: () => void;
  items: any[];
  activeItemIndex: number;
  onClickIndicator: (index: number) => void;
}

const Controls = ({
  onClickPrev,
  onClickNext,
  items,
  activeItemIndex,
  onClickIndicator
}: ControlsProps) => (
  <Fragment>
    <CarouselControl left onClick={onClickPrev}>
      <SvgIcon icon={'arrow'} />
    </CarouselControl>
    <CarouselControl right onClick={onClickNext}>
      <SvgIcon icon={'arrow'} />
    </CarouselControl>
    <CarouselIndicatorWrapper>
      <Section>
        <CarouselIndicators>
          {items.map((item, index) => (
            <CarouselIndicatorItem
              key={index}
              data-id={index}
              activeItem={activeItemIndex === index}
              onClick={() => onClickIndicator(index)}
            />
          ))}
        </CarouselIndicators>
      </Section>
    </CarouselIndicatorWrapper>
  </Fragment>
);

interface CarouselContainerProps {
  duration?: number;
  direction?: 'forward' | 'backward';
  autoplay?: boolean;
  isRoot?: boolean;
}

interface CarouselContainerState {
  activeItem: number;
  startPosition: number;
  endPosition: number;
  items: CType[];
}

class CarouselContainer extends PureComponent<
  CarouselContainerProps & WithBreakpointsProps & WithTypo3Props,
  CarouselContainerState
> {
  public static getDerivedStateFromProps(
    nextProps: CarouselContainerProps & WithBreakpointsProps & WithTypo3Props,
    state: CarouselContainerState
  ): Partial<CarouselContainerState> | null {
    const stage = selectStage(nextProps.typo3);
    if (stage !== state.items) {
      return {
        items: stage
      };
    }
    return null;
  }

  public state = {
    activeItem: 0,
    startPosition: 0,
    endPosition: 0,
    items: selectStage(this.props.typo3)
  };

  public sliderRef = createRef<Slider>();

  public afterSliderChange = (activeItem: number) => {
    this.setState({ activeItem });
  };

  public nextItem = () => {
    const { current } = this.sliderRef;
    current && current.slickNext();
  };

  public prevItem = () => {
    const { current } = this.sliderRef;
    current && current.slickPrev();
  };

  public onClickNext = () => {
    this.nextItem();
  };

  public onClickPrev = () => {
    this.prevItem();
  };

  public onClickIndicator = (index: number) => {
    const { current } = this.sliderRef;
    current && current.slickGoTo(index);
  };

  public render() {
    const { activeItem, items } = this.state;
    const {
      isRoot = false,
      autoplay = true,
      duration = 10,
      breakpoints
    } = this.props;
    const { large } = breakpoints;

    return (
      <Carousel isRoot={isRoot} className="hide-for-print">
        <Slider
          arrows={false}
          dots={false}
          autoplay={autoplay}
          autoplaySpeed={duration * 1000}
          slidesToScroll={1}
          slidesToShow={1}
          adaptiveHeight={false}
          ref={this.sliderRef}
          afterChange={this.afterSliderChange}
          lazyLoad="ondemand"
        >
          {items.map((item, i) => {
            const images: Typo3Image[] =
              item.files && item.files.image
                ? !large && item.files.image_mobile
                  ? item.files.image_mobile
                  : item.files.image
                : [];

            const [image] = images;
            return (
              <CarouselItem key={i} id={i.toString()}>
                {image && (
                  <CarouselImage isRoot={isRoot}>
                    {/* Specify size to avoid Layout Shift while building the slider #46960 */}
                    <TypoImage image={image} alt={item.header} width='1920' height='790' />
                  </CarouselImage>
                )}
                {item.CType === 'slider_textpic' && <Content {...item} />}
              </CarouselItem>
            );
          })}
        </Slider>
        {items.length > 1 && (
          <Controls
            onClickNext={this.onClickNext}
            onClickPrev={this.onClickPrev}
            onClickIndicator={this.onClickIndicator}
            items={items}
            activeItemIndex={activeItem}
          />
        )}
      </Carousel>
    );
  }
}

export default withBreakpoints(
  withTypo3<CarouselContainerProps & WithBreakpointsProps>(CarouselContainer)
);
